import { cloneDeep } from 'lodash';
import { MarkerBaseState } from 'markerjs2';
import { DisplayElementModel } from '../../PageBuilder/models/display-element.model';
import { ReportSeriesModel } from './report-series.model';
import { DimensionsModel } from '../../Misc/models/dimensions.model';
import { LabelDisplayOptionsEnum } from '../../Misc/enums/label-display-options.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { ChartStylesModel } from '../../Charts/models/chart-styles.model';
import { ChartMetricsEnum } from '../enums/chart-metrics.enum';
import { externalDataSeriesTypes, mentionSeriesTypes, socialSeriesTypes } from '../enums/series-type.enum';
import { ReportBlockTypeEnum, reportChartInfoMap } from '../enums/report-block-type.enum';
import { GoogleAnalyticsReportTypesEnum } from '../../../api/externalData/enums/google-analytics-report-types.enum';
import { PREventModel } from './pr-event.model';
import { ChartContentFilterModel } from '../../Dashboard/models/chart-content-filter.model';
import { ChartDisplaySettingsModel } from '../../Charts/models/chart-display-settings.model';
import { ChartSpecificTypesEnum } from '../enums/chart-specific-types.enum';
import { TermTypesEnum } from '../enums/term-types.enum';
import { WordCloudSeriesModel } from '../../Charts/models/WordCloudChart/word-cloud-series.model';
import { MapChartChoroplethInterpolationTypeEnum, MapChartColorSchemeEnum } from '../../Charts/models/map-chart.model';
import { ReportDateRangeModel } from './report-date-range.model';
import { ChartTimeIntervalsEnum } from '../enums/chart-time-intervals.enum';
import { MentionCustomizationModel } from '../../../Monitor/Shared/models/mention-customization.model';
import { Mention } from '../../Mentions/models/mention';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortableMentionBlockTypeEnum } from '../enums/sortable-mention-block-type.enum';
import { StackedChartTypesEnum } from '../enums/stacked-chart-types.enum';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { directionsEnum } from '../../../Common/Misc/enums/directions.enum';
import { ChartAnnotations } from './chart-annotations.model';


export enum ChartValidSourceStateEnum {
    NONE = 'NONE',
    SOME = 'SOME',
    ALL = 'ALL'
}

export class ChartDisplayElementModel extends DisplayElementModel {
    declare styles: ChartStylesModel;

    size: DimensionsModel;
    title: string;
    dynamicTitle?: boolean;
    dateRangeLabel?: string;
    _series: ReportSeriesModel[] | WordCloudSeriesModel[];
    metric: ChartMetricsEnum;
    metrics: ChartMetricsEnum[];
    sentiments: SearchCriteriaQuerySentimentsEnum[];
    referralTypes: GoogleAnalyticsReportTypesEnum[];
    prevents: PREventModel[];
    enableDataDisplaySettings: boolean = true;
    labelDisplayOptions: LabelDisplayOptionsEnum[];
    stackedChartTypes?: StackedChartTypesEnum;
    index?: number;
    note?: string;
    seriesCount?: number;
    originalReportBlockId?: number;
    enableEditingNote?: boolean;
    numOfItems: number = 5;
    contentFilters: ChartContentFilterModel = new ChartContentFilterModel();
    chartHeaderSettings: ChartDisplaySettingsModel;
    networkSelection?: MediaTypeEnum[];
    termType?: TermTypesEnum;
    chartSpecificType?: ChartSpecificTypesEnum;
    mapChartColorScheme?: MapChartColorSchemeEnum;
    mapChartChoroplethInterpolationType?: MapChartChoroplethInterpolationTypeEnum;
    dateRange?: ReportDateRangeModel;
    timeInterval?: ChartTimeIntervalsEnum;
    topMentionsSortField?: SearchCriteriaSortFieldEnum;
    topMentionsMetadata?: MentionCustomizationModel;
    topCuratedMentions?: Mention[];
    trackedColorChanged?: boolean;
    legendPosition?: directionsEnum;
    displayAnnotationInExport?: boolean;
    chartAnnotations?: ChartAnnotations;

    set series(series: ReportSeriesModel[]) {
        const externalDataSeries = this._series?.filter(s => externalDataSeriesTypes.includes(s.type));
        if (externalDataSeries?.length > 0) {
            this._series = [
                ...series.filter(s => !externalDataSeriesTypes.includes(s.type)),
                ...externalDataSeries
            ];
        } else {
            this._series = series;
        }

        if (!this._series || !this._series.length) {
            return;
        }

        if (!this._series[0].styles) {
            return;
        }

        if (!this.styles) {
            this.styles = new ChartStylesModel();
        }

        if (this._series[0].styles.geographyScale) {
            this.styles.chartGeographyType = this._series[0].styles.geographyScale;
            this.styles.regionIsoCode = null;
        }

        if (this._series[0].styles.regionIsoCode) {
            this.styles.regionIsoCode = this._series[0].styles.regionIsoCode;
        }

        this.dynamicTitle = reportChartInfoMap.get(this.series[0]?.chartType)?.dynamicTitle ?? false;
    }

    get series(): ReportSeriesModel[] {
        return this._series;
    }

    //#region Helper getters/setters for page builder forms
    get mentionsSeries(): ReportSeriesModel[] {
        return this.getMentionSeries();
    }
    set mentionsSeries(series: ReportSeriesModel[]) {
        this.setMentionSeries(series);
    }
    get externalDataSeries(): ReportSeriesModel[] {
        return this.getExternalDataSeries();
    }
    set externalDataSeries(series: ReportSeriesModel[]) {
        this.setExternalDataSeries(series);
    }
    //#endregion

    constructor(title: string, series: ReportSeriesModel[], styles: ChartStylesModel) {
        super();

        this.title = title;
        this.series = series || [];
        this.styles = styles || new ChartStylesModel();
        this.sentiments = [];
        this.referralTypes = [];
        this.chartHeaderSettings = this.styles.chartDisplaySettings;
        this.dynamicTitle = false;

        // assumption: all charts want to display 'percent' for their data elements by default
        if (
            this._series && this._series?.length &&
            (
                this._series[0].chartType === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK
                || this._series[0].chartType === ReportBlockTypeEnum.REFERRAL_TREND
            )
        ) {
            this.labelDisplayOptions = [
                LabelDisplayOptionsEnum.count
            ];
        } else {
            this.labelDisplayOptions = [
                LabelDisplayOptionsEnum.percent
            ];
        }
    }

    canRender(): boolean {
        return true;
    }

    clone(): ChartDisplayElementModel {
        const copy = new ChartDisplayElementModel(
            this.title,
            this.series.map(series => series.clone()),
            this.styles.clone()
        );
        copy.size = this.size;
        copy.dateRangeLabel = this.dateRangeLabel;
        copy.referralTypes = this.referralTypes;
        copy.prevents = cloneDeep(this.prevents); // does not re-draw in the preview
        copy.sentiments = this.sentiments;
        copy.labelDisplayOptions = this.labelDisplayOptions;
        copy.stackedChartTypes = this.stackedChartTypes;
        copy.networkSelection = this.networkSelection;
        copy.metric = this.metric;
        copy.metrics = this.metrics?.slice();
        copy.seriesCount = this.seriesCount;
        copy.numOfItems = this.numOfItems;
        copy.dynamicTitle = this.dynamicTitle;
        copy.contentFilters = this.contentFilters.clone();
        copy.chartHeaderSettings = new ChartDisplaySettingsModel(this.chartHeaderSettings);
        copy.termType = this.termType;
        copy.chartSpecificType = this.chartSpecificType;
        copy.mapChartColorScheme = this.mapChartColorScheme;
        copy.mapChartChoroplethInterpolationType = this.mapChartChoroplethInterpolationType;
        copy.dateRange = this.dateRange;
        copy.timeInterval = this.timeInterval;
        copy.topMentionsSortField = this.topMentionsSortField;
        copy.topMentionsMetadata = this.topMentionsMetadata;
        copy.topCuratedMentions = this.topCuratedMentions;
        copy.displayType = this.displayType;
        copy.trackedColorChanged = this.trackedColorChanged;
        copy.legendPosition = this.legendPosition;
        copy.displayAnnotationInExport = this.displayAnnotationInExport;
        copy.chartAnnotations = this.chartAnnotations?.clone();
        return copy;
    }

    onUpdate(): boolean {
        this.chartHeaderSettings.multiSource = this.series?.length > 1;
        return true;
    }

    isEqual(other: ChartDisplayElementModel): boolean {
        if (this.title !== other.title) {
            return false;
        }
        if (JSON.stringify(this.size) !== JSON.stringify(other.size)) {
            return false;
        }
        if (this.series.length !== other.series.length) {
            return false;
        }
        for (let i = 0; i < this.series?.length; i++) {
            if (!this.series[i].isEqual(other.series[i])) {

                return false;
            }
        }
        if (!this.styles.equals(other.styles, true)) {
            return false;
        }
        if (JSON.stringify(this.sentiments) !== JSON.stringify(other.sentiments)) {
            return false;
        }
        if (JSON.stringify(this.referralTypes) !== JSON.stringify(other.referralTypes)) {
            return false;
        }
        if (JSON.stringify(this.prevents) !== JSON.stringify(other.prevents)) {
            return false;
        }
        if (JSON.stringify(this.labelDisplayOptions) !== JSON.stringify(other.labelDisplayOptions)) {
            return false;
        }
        if (JSON.stringify(this.stackedChartTypes) !== JSON.stringify(other.stackedChartTypes)) {
            return false;
        }
        if (JSON.stringify(this.networkSelection) !== JSON.stringify(other.networkSelection)) {
            return false;
        }

        if (JSON.stringify(this.metric) !== JSON.stringify(other.metric)) {
            return false;
        }

        if (JSON.stringify(this.termType) !== JSON.stringify(other.termType)) {
            return false;
        }

        if (JSON.stringify(this.chartSpecificType) !== JSON.stringify(other.chartSpecificType)) {
            return false;
        }

        if (JSON.stringify(this.mapChartColorScheme) !== JSON.stringify(other.mapChartColorScheme)) {
            return false;
        }

        if (JSON.stringify(this.mapChartChoroplethInterpolationType) !== JSON.stringify(other.mapChartChoroplethInterpolationType)) {
            return false;
        }

        if (JSON.stringify(this.topMentionsSortField) !== JSON.stringify(other.topMentionsSortField)) {
            return false;
        }

        if (this.topMentionsMetadata && !this.topMentionsMetadata.equals(other.topMentionsMetadata, false, false, false, false)) {
            return false;
        }

        if (!!this.topCuratedMentions) {
            for (let i = 0; i < this.topCuratedMentions.length; i++) {
                if (this.topCuratedMentions[i].id !== other.topCuratedMentions[i].id) {
                    return false;
                }
            }
        }

        if (JSON.stringify(this.metrics) !== JSON.stringify(other.metrics)) {
            return false;
        }

        if (JSON.stringify(this.timeInterval) !== JSON.stringify(other.timeInterval)) {
            return false;
        }

        if (this.seriesCount !== other.seriesCount) {
            return false;
        }

        if (this.numOfItems !== other.numOfItems) {
            return false;
        }

        if (this.trackedColorChanged !== other.trackedColorChanged) {
            return false;
        }

        if (this.legendPosition !== other.legendPosition) {
            return false;
        }

        if (!this.contentFilters.isEqual(other.contentFilters)) {
            return false;
        }

        if (!this.chartHeaderSettings.isEqual(other.chartHeaderSettings)) {
            return false;
        }

        // used in dashboard
        if (!!this.dateRange && !this.dateRange.isEqual(other?.dateRange)) {
            return false;
        }

        if (this.settings) {
            if (
                this.settings.altText !== other?.settings?.altText ||
                this.settings.caption !== other?.settings?.caption ||
                this.settings.displaySize !== other?.settings?.displaySize
            ) {
                return false;
            }
        }

        if (this.displayAnnotationInExport !== other.displayAnnotationInExport) {
            return false;
        }

        const chartAnnotations = this.chartAnnotations ?? new ChartAnnotations();
        if (!chartAnnotations.isEqual(other?.chartAnnotations ?? new ChartAnnotations())) {
            return false;
        }

        return true;
    }

    getMentionSeries(): ReportSeriesModel[] {
        return this._series.filter(s => mentionSeriesTypes.includes(s.type));
    }

    getSocialSeries(): ReportSeriesModel[] {
        return this._series.filter(s => socialSeriesTypes.includes(s.type));
    }

    setMentionSeries(mentionSeries: ReportSeriesModel[]): void {
        const otherSeries = this._series.filter(s => !mentionSeriesTypes.includes(s.type));

        this._series = [
            ...mentionSeries,
            ...otherSeries
        ];
    }

    setSocialSeries(socialSeries: ReportSeriesModel[]): void {
        const otherSeries = this._series.filter(s => !socialSeriesTypes.includes(s.type));

        this._series = [
            ...socialSeries,
            ...otherSeries
        ];
    }

    getExternalDataSeries(): ReportSeriesModel[] {
        return this._series.filter(s => externalDataSeriesTypes.includes(s.type));
    }

    setExternalDataSeries(googleAnalyticsSeries: ReportSeriesModel[]): void {
        const otherSeries = this._series.filter(s => !externalDataSeriesTypes.includes(s.type));

        this._series = [
            ...otherSeries,
            ...googleAnalyticsSeries
        ];
    }

    /**
     * Replaces all series, including external data series.
     * Unlike the `series` setter, existing external data series are not preserved.
     */
    replaceAllSeries(series: ReportSeriesModel[]): void {
        this._series = [];
        this.series = series;
    }

    areAllSourcesValidForChartType(chartType: ReportBlockTypeEnum): boolean {
        const chartInfo = reportChartInfoMap.get(chartType);

        if (chartInfo.mentions) {
            const mentionsSeries = this.getMentionSeries();
            const socialSeries = this.getSocialSeries();

            if ((mentionsSeries.length < 1) && (socialSeries.length < 1)) {
                return false;
            }
            if (mentionsSeries.some(s => !(s.hasValidMentionsSource() || this.isCustomSortFieldValid(s.chartType))) ||
                socialSeries.some(s => !(s.hasValidSocialSource() || this.isCustomSortFieldValid(s.chartType)))
            ) {
                return false;
            }
        }

        if (chartInfo.externalData) {
            const analyticsSeries = this.getExternalDataSeries();
            if (analyticsSeries.length < 1) {
                return false;
            }
            if (analyticsSeries.find(s => !s.hasValidExternalSource())) {
                return false;
            }
        }

        return true;
    }

    isCustomSortFieldValid(chartType: ReportBlockTypeEnum): boolean {
        return SortableMentionBlockTypeEnum[chartType] &&
            this.topMentionsSortField === SearchCriteriaSortFieldEnum.custom &&
            this.topCuratedMentions?.length > 0;
    }

    checkValidSourcesForChartType(chartType: ReportBlockTypeEnum): ChartValidSourceStateEnum {
        const chartInfo = reportChartInfoMap.get(chartType);

        if (chartInfo.mentions) {
            const mentionsSeries = this.getMentionSeries();
            const socialSeries = this.getSocialSeries();
            const validMentionSources = mentionsSeries.filter(s => s.hasValidMentionsSource());
            const validSocialSources = socialSeries.filter(s => s.hasValidSocialSource());
            if (validMentionSources.length === 0 && validSocialSources.length === 0) {
                return ChartValidSourceStateEnum.NONE;
            } else if (validMentionSources.length < mentionsSeries.length || validSocialSources.length < socialSeries.length) {
                return ChartValidSourceStateEnum.SOME;
            }
        }

        if (chartInfo.externalData) {
            const analyticsSeries = this.getExternalDataSeries();
            const validAnalyticsSeries = analyticsSeries.filter(s => s.hasValidExternalSource());
            if (validAnalyticsSeries.length === 0) {
                return ChartValidSourceStateEnum.NONE;
            } else if (validAnalyticsSeries.length < analyticsSeries.length) {
                return ChartValidSourceStateEnum.SOME;
            }
        }

        return ChartValidSourceStateEnum.ALL;
    }
}
